/**
 * Template variable converter utility
 * Converts template variables like ${Type} to appropriate default values
 */

/**
 * Get current time in HH:MM format
 */
const getCurrentTime = () => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Get today's date in YYYY/MMM/DD format
 */
const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const month = monthNames[now.getMonth()];
  const day = now.getDate().toString().padStart(2, "0");
  return `${year}/${month}/${day}`;
};

/**
 * Get default temperature based on scope preference
 */
const getDefaultTemperature = (scopePreference) => {
  // Check scope preference for temperature unit
  const useFahrenheit = scopePreference?.temperatureUnit === "F";

  return useFahrenheit ? "77F" : "25C";
};

/**
 * Get default temperature difference based on scope preference
 */
const getDefaultTemperatureDiff = (scopePreference) => {
  const useFahrenheit = scopePreference?.temperatureUnit === "F";

  return useFahrenheit ? "18F" : "10C";
};

/**
 * Convert template variable to default value based on type
 */
const convertTemplateVariable = (variableName, scopePreference = null) => {
  // Check if variable contains enum values (format: variableName:value1,value2,value3)
  if (variableName.includes(":")) {
    const colonIndex = variableName.indexOf(":");
    const enumValues = variableName.substring(colonIndex + 1);
    const firstValue = enumValues.split(",")[0].trim();
    return firstValue;
  }

  const normalizedName = variableName.toLowerCase();

  // Number types
  if (normalizedName.includes("number")) {
    return "0";
  }

  // Boolean types
  if (normalizedName.includes("bool")) {
    return "false";
  }

  // String types
  if (normalizedName.includes("string")) {
    return "[text here]";
  }

  // Time types
  if (normalizedName.includes("time")) {
    return getCurrentTime();
  }

  // Date types
  if (normalizedName.includes("date")) {
    return getTodayDate();
  }

  // Temperature types
  if (
    normalizedName.includes("temperature") &&
    !normalizedName.includes("diff")
  ) {
    return getDefaultTemperature(scopePreference);
  }

  // Temperature difference types
  if (normalizedName.includes("temperaturediff")) {
    return getDefaultTemperatureDiff(scopePreference);
  }

  // Duration types
  if (
    normalizedName.includes("duration") ||
    (normalizedName.includes("time") && normalizedName.includes("span"))
  ) {
    return "5min";
  }

  // Default fallback - return empty string for unknown types
  return "";
};

/**
 * Process template string and replace all ${Variable} patterns with default values
 */
export const processTemplateVariables = (template, scopePreference = null) => {
  if (!template) return template;

  // Find all template variables like ${Variable}
  const templateRegex = /\$\{([^}]+)\}/g;

  return template.replace(templateRegex, (match, variableName) => {
    return convertTemplateVariable(variableName.trim(), scopePreference);
  });
};

/**
 * Check if a string contains template variables
 */
export const hasTemplateVariables = (text) => {
  if (!text) return false;
  return /\$\{[^}]+\}/.test(text);
};

export default {
  processTemplateVariables,
  hasTemplateVariables,
  convertTemplateVariable,
};
